// @ts-nocheck
/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
// import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext"; // Starter Custom Code
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
// import React, { ChangeEvent, useEffect, useState } from "react"; // Starter Custom Code
import { connect } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCanAddToCart, useHasEnoughInventory } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import React, { ChangeEvent, useContext, useEffect, useState } from "react";

export interface ProductQuantityOrderedProps extends Omit<OwnProps, keyof TextFieldPresentationProps> {}

export const useShowProductQuantityOrdered = (
    configurationCompleted: boolean | undefined,
    variantSelectionCompleted: boolean | undefined,
) => {
    const productContext = useContext(ProductContext);
    const { product, productInfo } = productContext;
    const hasEnoughInventory = useHasEnoughInventory(productContext);
    const canAddToCart = useCanAddToCart(product, productInfo, configurationCompleted, variantSelectionCompleted);

    return !((!hasEnoughInventory && !productInfo.failedToLoadInventory) || !canAddToCart);
};
/* Starter Custom Code - End */

// Foundation -->
import { useIsSignedInAndhasToBeSignedIn } from "@store/Framework/Store/Context/ContextHooks";
import SmallHeadingAndText from "@store/Overrides/Components/SmallHeadingAndText";

export interface ProductQuantityOrderedProps extends Omit<OwnProps, keyof TextFieldPresentationProps> {}
// <-- Foundation

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    // Starter Custom Code
    overridedStyles?: TextFieldPresentationProps;
    // Foundation
    canEdit?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    sizeVariant: "small",
    cssOverrides: {
        descriptionWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.descriptionWrapper}
        `,
        formField: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formField}
            width: auto;
        `,
        formInputWrapper: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.formInputWrapper}
        `,
        inputSelect: css`
            ${x => x.theme.formField.defaultProps?.cssOverrides?.inputSelect}
            width: ${pxToRem(55)};
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    overridedStyles, // Starter Custom Code
    onBlur = () => {}, // Starter Custom Code
    canEdit = true, // Foundation
    ...otherProps
}: Props) => {
    const styles = useExpandProps(productQuantityOrderedStyles, extendedStyles, overridedStyles); // Starter Custom Code
    const [productQty, setProductQty] = useState(qtyOrdered.toString());

    // Foundation -->
    const isSignedInAndhasToBeSignedIn = useIsSignedInAndhasToBeSignedIn();
    // <-- Foundation

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if (!useShowProductQuantityOrdered(configurationCompleted, variantSelectionCompleted) /* Starter Custom Code */) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    // Foundation --->
    if (!isSignedInAndhasToBeSignedIn) {
        return null;
    }
    // <--- Foundation

    // Foundation --->
    if (canEdit) {
        return (
            <TextField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                onBlur={onBlur} // Starter Custom Code
                {...styles}
            />
        );
    }

    return <SmallHeadingAndText heading={(labelOverride as string) ?? translate("QTY")} text={productQty} />;
    // <--- Foundation
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
